import axios from "axios";
import { API_BASE_URL } from "../config";
import { getAccessToken, clearSession } from "../utils/tokenStorage";
import { refreshSession } from "./refreshAuth";

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
});

const AUTH_SKIP_REFRESH = [
  "/api/auth/login",
  "/api/auth/refresh",
  "/api/auth/logout",
  "/api/auth/mfa/verify",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
];

const shouldSkipRefresh = (url = "") =>
  AUTH_SKIP_REFRESH.some((path) => url.includes(path));

let isRefreshing = false;
let refreshWaiters = [];

const resolveWaiters = (error, token = null) => {
  refreshWaiters.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshWaiters = [];
};

const redirectToLogin = () => {
  clearSession();
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
};

axiosClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !original ||
      original._retry ||
      shouldSkipRefresh(original.url || "")
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshWaiters.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return axiosClient(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const accessToken = await refreshSession();
      resolveWaiters(null, accessToken);
      original.headers.Authorization = `Bearer ${accessToken}`;
      return axiosClient(original);
    } catch (refreshError) {
      resolveWaiters(refreshError, null);
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosClient;
